import { computed, type MaybeRefOrGetter, ref, toValue, watch } from "vue"
import type { Router } from "vue-router"
import type { ResultOf } from "@graphql-typed-document-node/core"
import { useQuery } from "@vue/apollo-composable"
import { extractTags, type SchemaField } from "@auger/core"
import { computeRelationChanges, enrichField, recordFixedFields } from "@auger/core/collections"
import { type AugerField, isRelationField } from "@auger/core/fields"
import { recordBySlug } from "@auger/core/gql"
import type { RecordFieldsFragment as RecordRow } from "@auger/core/gql/types"
import { fitSlug, slugify } from "@auger/ui/utils/string"
import { getAuger } from "@/init/auger"
import { getSchema } from "@/stores/schemas"
import { getType } from "@/stores/types"
import { errorMessage } from "@/utils/errors"
import type { ComposableDeps } from "./types"
import { useSchema } from "./useSchema"
import { useSources } from "./useSources"
import { arbitrate } from "./writeArbiter"

// A record as useRecord loads it: the FullRecordFields row fetchRecordBySlug
// returns, relations included.
export type LoadedRecord = ResultOf<typeof recordBySlug>["records"][number]

/**
 * A live useRecord instance: the loaded row plus everything the page can do
 * with it — `fields`, `updateField`, `update`, `refresh`, tags, sources,
 * archiving.
 *
 * Blocks and toolbars are handed one of these rather than a bare row (see
 * BlockContainer), so the row is `record.record.value`, not `record`. The name
 * says composable for exactly that reason: a plain noun would imply a plain
 * object and hide the unwrap. (`Record` is TypeScript's anyway, and
 * `RecordContext` already means the chat payload in useClaude.)
 */
export type RecordComposable = ReturnType<typeof useRecord>

export interface UseRecordOptions {
  type?: MaybeRefOrGetter<string | null | undefined>
  slug?: MaybeRefOrGetter<string | null | undefined>
  /** Hold the current record while the next slug loads, not null in between. */
  keepPrevious?: boolean
}

export interface UseRecordDeps extends ComposableDeps {
  router?: Router | null
}

/**
 * Composable for managing a single record's state and operations.
 *
 * @example
 * const { record: person, loading, error, ... } = useRecord(
 *   { type: "person", slug: () => props.slug },
 *   { client, toast, router }
 * )
 */
export function useRecord(options: UseRecordOptions, deps: UseRecordDeps) {
  const { client, toast, router } = deps
  const { type: typeOption, slug: slugOption } = options

  const getTypeName = () => toValue(typeOption)
  const getSlug = () => toValue(slugOption)

  // --- State ---
  const isUpdating = ref(false)

  // Pass deps to child composables
  const composableDeps = { client, toast }

  // Live Apollo cache observer. Any mutation that updates the records cache
  // entry (or any refetchQueries({ include: ["fetchRecordBySlug"] }) call)
  // propagates here automatically — that's how junction-row writes reach the
  // record's chips.
  const {
    result,
    loading,
    error,
    refetch: refetchQuery,
  } = useQuery(
    recordBySlug,
    // Coerced to "" when absent; the `enabled` guard blocks the query from
    // running until both are present, so the empties are never sent.
    () => ({ type: getTypeName() ?? "", slug: getSlug() ?? "" }),
    () => ({
      enabled: !!(getTypeName() && getSlug()),
      fetchPolicy: "cache-and-network",
      keepPreviousResult: !!options.keepPrevious,
    })
  )

  const record = computed<LoadedRecord | null>(() => result.value?.records?.[0] ?? null)

  // Use useSchema for type schema (inherited fields for all records of this type)
  const typeSchemaRef = computed(() => {
    const recordType = record.value?.type
    if (!recordType) return null
    return getType(recordType)?.schema || null
  })
  const typeSchema = useSchema(typeSchemaRef, composableDeps)

  // Use useSchema for record schema (additional fields specific to this record).
  // Resolved via the schemas store by id since fetchRecordBySlug doesn't
  // include the nested schema relation.
  const recordSchemaRef = computed(() =>
    record.value?.schema_id ? getSchema(record.value.schema_id) : null
  )
  const recordSchema = useSchema(recordSchemaRef, composableDeps)

  // --- Fields ---
  // Three sources, all enriched at construction: schema fields read from
  // record.data; fixed fields read top-level columns. enrichField attaches the
  // id→entity resolver for reference/junction types — without it owner_person_id et al.
  // render "Unknown ID".

  // Inherited from the record's type.
  const typeSchemaFields = computed(() => {
    const schema = typeSchema.schema.value
    if (!schema?.fields) return []
    return schema.fields.map((f: SchemaField) =>
      enrichField(f, {
        schema_id: schema.id,
        isEditable: true,
        value: (item: unknown) =>
          (item as { data?: Record<string, unknown> } | null)?.data?.[f.key],
      })
    )
  })

  // Specific to this record.
  const recordSchemaFields = computed(() => {
    const schema = recordSchema.schema.value
    if (!schema?.fields) return []
    return schema.fields.map((f: SchemaField) =>
      enrichField(f, {
        schema_id: schema.id,
        isEditable: true,
        value: (item: unknown) =>
          (item as { data?: Record<string, unknown> } | null)?.data?.[f.key],
      })
    )
  })

  // The record's own columns (name, owner_person_id, …). recordFixedFields already
  // carries a top-level-column accessor, so we keep its value.
  const fixedFields = computed(() =>
    recordFixedFields.map((f) => enrichField(f, { value: f.value }))
  )

  // Merge + tag with `group` so detail views can section them, then dedupe by
  // key (first wins, recordSchema → recordColumn → typeSchema) — the type schema
  // and record schema coincide when a record is linked to its type's schema.
  const fields = computed<AugerField[]>(() => {
    const tagged: AugerField[] = [
      ...recordSchemaFields.value.map((f) => ({ ...f, group: "recordSchema" as const })),
      ...fixedFields.value.map((f) => ({ ...f, group: "recordColumn" as const })),
      ...typeSchemaFields.value.map((f) => ({ ...f, group: "typeSchema" as const })),
    ]
    const seen = new Set<string>()
    return tagged.filter((f) => {
      const key = (f.sourceKey ?? f.key) as string
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  })

  /** The loaded record's id — the thing half this app's queries are keyed on. */
  const id = computed(() => record.value?.id)

  // Child lists (records with type 'list' that are children of this record)
  const lists = computed(() => {
    return (record.value?.children || []).filter((child) => child.type === "list")
  })

  // Use useSources for source management
  const {
    sources,
    sourceFilter,
    add: addSource,
    remove: removeSource,
    fetch: refreshSources,
  } = useSources(record, composableDeps)

  // Useful shortcuts to common record properties

  // The record's Pipedrive id, from whichever pipedrive_* source it carries.
  const pipedriveId = computed<string | null>(
    () =>
      sources.value.find((source) => source.source_type.startsWith("pipedrive_"))?.source_id ?? null
  )

  // --- Data Fetching ---

  /**
   * Force-refetch the underlying useQuery. Type/slug changes are tracked
   * reactively by useQuery, so callers only need this for manual refresh
   * after side effects (e.g. setSchema) that don't auto-update the cache.
   * @returns {Promise<object>} Fetched record
   */
  function refresh() {
    return refetchQuery?.()?.then(() => record.value) ?? Promise.resolve(record.value)
  }

  // Surface query errors via toast, matching the previous imperative-fetch
  // behavior. useQuery handles retries internally.
  watch(error, (e) => {
    if (!e) return
    const typeName = getTypeName()
    const message = errorMessage(e)
    toast?.error?.(`Failed to load ${typeName || "record"}: ${message}`)
  })

  // --- Record Editing ---

  /**
   * Create an editable copy of this record. Excludes GraphQL relationship
   * fields (sources, contacts, listings, etc.) which should never be passed
   * to update() — except `tags`, which the edit UI displays and the mutation
   * layer strips out via `skipKeys` in `computeRecordChanges()`.
   */
  const createEditableRecord = () => {
    return {
      id: record.value?.id,
      type: record.value?.type,
      name: record.value?.name,
      slug: record.value?.slug || "",
      description: record.value?.description,
      visibility: record.value?.visibility,
      owner_person_id: record.value?.owner_person_id,
      schema_id: record.value?.schema_id,
      parent_id: record.value?.parent_id,
      data: { ...(record.value?.data || {}) },
      tags: extractTags(record.value?.taggings),
    }
  }

  // Update the record with a flat fields object. Only changed fields are sent
  // to Hasura — undefined values are skipped (no accidental nulling). Fixed
  // fields (name, slug, description, visibility, owner_person_id) update the record
  // directly; everything else updates `record.data[key]`.
  //
  // Relation fields (link/tagging/attachment/contact/time) are excluded here:
  // their value lives in their own schema_key-scoped rows, reconciled by
  // `updateField` (core's relation planner) or persisted by their bespoke input
  // (ContactsInput). The read path resolves from those rows, never from
  // record.data[key] — so writing a relation field's ids into data would be dead,
  // stale data. We strip those keys and let the rows stand as the source of truth.
  const update = (changes: Record<string, unknown>, updateOptions: { silent?: boolean } = {}) => {
    const { silent = false } = updateOptions

    if (!record.value?.id) {
      toast?.error?.("Cannot update: no record loaded")
      return Promise.resolve(false)
    }

    const dataChanges: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(changes)) {
      const field = fields.value.find((f) => (f.sourceKey ?? f.key) === key)
      if (field && isRelationField(field)) continue // reconciled as rows, not data
      dataChanges[key] = val
    }

    // Nothing left to persist to records.data — the only edits were relation
    // fields, already reconciled as rows by their inputs. Report as a no-op.
    if (Object.keys(dataChanges).length === 0) {
      return Promise.resolve(record.value)
    }

    const slugChanging = dataChanges.slug && dataChanges.slug !== record.value.slug

    isUpdating.value = true

    const id = record.value.id
    return getAuger(client)
      .records.update(id, dataChanges, { read: false })
      .then(() => {
        // Apollo's normalized cache auto-merges the mutation result into the
        // cached records:N entry, so `record` (a useQuery-backed computed)
        // updates reactively without a manual assignment here.

        if (!silent) {
          toast?.success?.(`Updated ${record.value?.name}`)
        }

        // Navigate to new slug if it changed
        if (slugChanging && router) {
          router.replace({
            name: "record-show",
            params: { recordType: record.value?.type, slug: changes.slug as string },
          })
        }

        return record.value
      })
      .catch((err) => {
        const error = errorMessage(err)
        console.error(`Error updating record:`, err)
        toast?.error?.(`Failed to update ${record.value?.name ?? "record"}: ${error}`)
        return false
      })
      .finally(() => {
        isUpdating.value = false
      })
  }

  // Update a single field, silent by default (inline edits). Relation fields
  // reconcile their `schema_key`-scoped rows through core's planner (the
  // grid/card path); everything else is a data/column write.
  const updateField = (fieldKey: string, value: unknown) => {
    const field = fields.value.find((f) => (f.sourceKey ?? f.key) === fieldKey)
    if (field && isRelationField(field)) return reconcileRelationField(field, value)
    return update({ [fieldKey]: value }, { silent: true })
  }

  // Reconcile a relation field to the target-id set (bulk add + remove), then
  // refetch to converge — `record` is a useQuery computed, so there's no
  // optimistic local patch.
  const reconcileRelationField = async (field: AugerField, value: unknown) => {
    const rec = record.value
    if (!rec?.id) {
      toast?.error?.("Cannot update: no record loaded")
      return Promise.resolve(false)
    }

    // Normalize to a target-id set (tolerating `{ id }` objects); skip anything
    // non-id (e.g. a history-restore snapshot) rather than corrupt rows.
    const draft = value == null ? [] : Array.isArray(value) ? value : [value]
    const ids = draft.map((v) => (v != null && typeof v === "object" && "id" in v ? v.id : v))
    const isIdSet = ids.every((id) => typeof id === "number" || typeof id === "string")
    if (!isIdSet) return Promise.resolve(rec)

    const targets = ids.map((id) => ({ id: id as number | string }))
    const changes = arbitrate(rec.name ?? "record", toast, () =>
      computeRelationChanges(field, rec, targets)
    )
    if (!changes.add.length && !changes.removeRowIds.length) return Promise.resolve(rec)

    isUpdating.value = true
    return (async () => {
      try {
        const auger = getAuger(client)
        await auger.records.update(
          rec.id,
          { [field.sourceKey ?? field.key]: targets },
          { read: false }
        )
        await refresh()
        return record.value
      } catch (err) {
        const error = errorMessage(err)
        toast?.error?.(`Failed to update ${rec.name ?? "record"}: ${error}`)
        await refresh().catch(() => undefined)
        return false
      } finally {
        isUpdating.value = false
      }
    })()
  }

  // Archive/unarchive. Unlike update(), these bypass the fixed/data-field
  // partition entirely — archived_at is deliberately not in RECORD_FIXED_KEYS,
  // so it's never editable through generic field editing. Apollo's normalized
  // cache auto-merges the returned archived_at into the cached record — same
  // as `update()` above, no manual assignment needed.
  const archiveRecord = () => {
    if (!record.value?.id) {
      toast?.error?.("Cannot archive: no record loaded")
      return Promise.resolve(false)
    }
    const id = record.value.id
    return getAuger(client)
      .records.archive(id)
      .then(() => true)
      .catch((err) => {
        const error = errorMessage(err)
        toast?.error?.(`Failed to archive: ${error}`)
        return false
      })
  }

  const unarchiveRecord = () => {
    if (!record.value?.id) {
      toast?.error?.("Cannot unarchive: no record loaded")
      return Promise.resolve(false)
    }
    const id = record.value.id
    return getAuger(client)
      .records.unarchive(id)
      .then(() => true)
      .catch((err) => {
        const error = errorMessage(err)
        toast?.error?.(`Failed to unarchive: ${error}`)
        return false
      })
  }

  // --- Schema Management ---

  // Link the record to a schema, or pass null to clear it.
  function setSchema(schemaId: number | null) {
    const current = record.value
    if (!current) {
      toast?.error?.("Cannot update schema: no record loaded")
      return Promise.resolve(null)
    }

    return getAuger(client)
      .records.update(current.id, { schema_id: schemaId }, { read: false })
      .then(() => {
        // The column write returns the record's schema_id, so Apollo's cache
        // merge updates record.schema_id; the schema itself is the catalog's.
        toast?.success?.(schemaId ? "Schema linked" : "Schema unlinked")
        return record.value
      })
      .catch((err) => {
        const error = errorMessage(err)
        toast?.error?.(`Failed to ${schemaId ? "link" : "unlink"} schema: ${error}`)
        throw err
      })
  }

  /**
   * Create a new list as a child of this record
   * @param {Object} listOptions - List creation options
   * @param {string} listOptions.recordType - The type of records this list will contain (person, organization, event)
   * @param {string} [listOptions.name] - Optional custom name for the list
   * @param {string} [listOptions.listingRole] - Optional role for listings in this list (e.g., "guest")
   * @param {string} [listOptions.visibility="shared"] - List visibility (private, shared, public)
   * @param {Function} [listOptions.onSuccess] - Optional callback when list is created successfully
   * @returns {Promise<Object|null>} The created list object or null on error
   */
  const createList = (
    listOptions: {
      recordType?: string
      name?: string | null
      listingRole?: string | null
      visibility?: string
      onSuccess?: (list: RecordRow) => void
    } = {}
  ) => {
    const {
      recordType: listRecordType,
      name: customName,
      listingRole = null,
      visibility = "shared",
      onSuccess,
    } = listOptions

    if (!listRecordType) {
      toast?.error?.("List record type is required")
      return Promise.resolve(null)
    }

    const current = record.value
    if (!current) {
      toast?.error?.("No record loaded")
      return Promise.resolve(null)
    }

    // Generate list name and slug
    const date = new Date()
    const dateString = date.toISOString().split(".")[0]!.replace("T", " ")
    const name = customName || `${current.name || "New"} list`

    isUpdating.value = true

    return getAuger(client)
      .records.create({
        type: "list",
        name,
        slug: fitSlug(slugify(name), slugify(dateString)),
        parent_id: current.id,
        visibility,
        data: {
          record_type: listRecordType,
          listing_role: listingRole,
          listing_template: null,
        },
      })
      .then((newList) => {
        toast?.success?.(`Created "${name}" list`)
        if (onSuccess && typeof onSuccess === "function") {
          onSuccess(newList)
        }
        return newList
      })
      .catch((err: unknown) => {
        console.error("Error creating list:", err)
        toast?.error?.(`Failed to create "${name}" list`)
        return null
      })
      .finally(() => {
        isUpdating.value = false
      })
  }

  return {
    // Data
    record,
    sources, // From useSources

    // State
    loading,
    error,
    isUpdating,

    // Computed Properties
    id,
    fields, // All fields: fixed + type schema + record schema
    lists,

    // Convenience computed props
    pipedriveId,

    // Methods - Data fetching
    refresh,

    // Methods - Record editing
    createEditableRecord,
    update,
    updateField,
    archiveRecord,
    unarchiveRecord,

    // Methods - Schema management
    setSchema,

    // Methods - Source management
    sourceFilter,
    addSource,
    removeSource,
    // Re-reads the source rows from the server. add/remove keep the list
    // correct optimistically; this is for writes that bypass them (detach &
    // revert, which goes through useSourceRevert).
    refreshSources,

    // Methods - List creation
    createList,
  }
}
